/**
 * Single source of truth for avatar identity: the initials shown when a user has
 * no usable profile image, and the colour deterministically derived from their
 * name. Shared by `UserAvatar` (React) and `handleErrorImg` (raw <img> fallback)
 * so both paths render the same avatar.
 *
 * Palette taken from the design reference (gold / red / teal are the three shown
 * in the mock); the rest keep the same saturation so any team size stays
 * visually consistent.
 */
export const AVATAR_COLORS = [
  '#D6A550',
  '#E05555',
  '#48A9A6',
  '#5E7CB8',
  '#A0709F',
  '#5FA88B',
  '#D97D55',
];

export const getInitials = (label) => {
  const words = String(label || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words.at(-1).charAt(0)).toUpperCase();
};

export const getAvatarColor = (label) => {
  const seed = String(label || '');
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.codePointAt(i)) % 100000;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const escapeXml = (value) =>
  String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

/**
 * Placeholder alt text used across the app for decorative avatars. Initials
 * derived from these would be meaningless ("I" for "img"), so callers should
 * fall back to the generic illustration instead.
 */
const GENERIC_LABELS = new Set([
  'img',
  'image',
  'images',
  'avatar',
  'photo',
  'logo',
  'icon',
  'user',
  'user_image',
  'userimage',
  'profile',
  'delegated',
]);

export const isNameLikeLabel = (label) => {
  const trimmed = String(label || '').trim();
  if (!trimmed) return false;
  return !GENERIC_LABELS.has(trimmed.toLowerCase());
};

/**
 * Initials avatar as an inline SVG data URI, for `<img>` elements that cannot
 * render a React fallback. The background fills the whole box so the call
 * site's own border-radius decides whether it reads as a circle or a square.
 */
export const getInitialsAvatarDataUri = (label) => {
  const initials = getInitials(label) || '?';
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">',
    `<rect width="64" height="64" fill="${getAvatarColor(label || initials)}"/>`,
    '<text x="32" y="32" dy="0.35em" text-anchor="middle" fill="#ffffff"',
    ' font-family="Raleway, Helvetica, Arial, sans-serif" font-size="26" font-weight="700">',
    escapeXml(initials),
    '</text></svg>',
  ].join('');

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};
